import { useState, type FormEvent } from "react";
import { Link } from "react-router-dom";
import { useMutation } from "@tanstack/react-query";
import { ArrowLeft, CheckCircle2, Download, Loader2 } from "lucide-react";
import {
  isValidLinkedInCompanyUrl,
  type IngestPrivateCompanyResponse,
  type PrivateCompany,
} from "../../../../../shared/index";
import { api, ApiError, isLikelyTimeoutError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { cn } from "@/lib/utils";

type Mode = "create" | "update";

const LINKEDIN_PLACEHOLDER = "https://www.linkedin.com/company/example-company/";

function describeError(e: unknown): string {
  return e instanceof ApiError ? e.message : "Something went wrong";
}

interface FieldErrors {
  companyName?: string;
  companyID?: string;
  linkedInURL?: string;
}

export default function PrivateCompaniesLinkedInPage() {
  const [mode, setMode] = useState<Mode>("create");

  // Form fields (kept per-purpose; linkedInURL is shared across both modes).
  const [companyName, setCompanyName] = useState("");
  const [companyID, setCompanyID] = useState("");
  const [linkedInURL, setLinkedInURL] = useState("");

  const [errors, setErrors] = useState<FieldErrors>({});
  const [banner, setBanner] = useState<string | null>(null);
  const [result, setResult] = useState<IngestPrivateCompanyResponse | null>(null);
  const [fetched, setFetched] = useState<PrivateCompany | null>(null);

  const resetFeedback = () => {
    setErrors({});
    setBanner(null);
    setResult(null);
  };

  const switchMode = (next: Mode) => {
    if (next === mode) return;
    setMode(next);
    resetFeedback();
    setFetched(null);
  };

  // ── Fetch existing company (update mode) ──────────────────────
  const fetchMutation = useMutation({
    mutationFn: (id: number) => api.privateCompanies.get(id),
    onSuccess: (data) => {
      setFetched(data.company);
      // Pre-fill the LinkedIn field with the existing URL for easy editing.
      setLinkedInURL(data.company.linkedIn ?? "");
      setErrors((e) => ({ ...e, companyID: undefined }));
      setBanner(null);
    },
    onError: (e) => {
      setFetched(null);
      setErrors((prev) => ({ ...prev, companyID: describeError(e) }));
      setBanner(describeError(e));
    },
  });

  const handleFetchCompany = () => {
    setResult(null);
    const id = Number(companyID.trim());
    if (!companyID.trim() || !Number.isInteger(id) || id <= 0) {
      setErrors((e) => ({ ...e, companyID: "Company ID must be numeric" }));
      return;
    }
    fetchMutation.mutate(id);
  };

  // ── Ingest (create / update) ──────────────────────────────────
  const ingestMutation = useMutation({
    mutationFn: api.privateCompanies.ingest,
    onSuccess: (data) => {
      setResult(data);
      if (data.success) {
        setBanner(null);
        setErrors({});
      } else {
        // Upstream business validation failure — surface the message and try
        // to highlight the field it refers to.
        const msg = data.errorMessage ?? "Request failed";
        setBanner(msg);
        const lower = msg.toLowerCase();
        setErrors({
          linkedInURL: lower.includes("linkedin") ? msg : undefined,
          companyID:
            mode === "update" && (lower.includes("id") || lower.includes("exist"))
              ? msg
              : undefined,
          companyName:
            mode === "create" && lower.includes("name") ? msg : undefined,
        });
      }
    },
    onError: (e) => {
      setResult(null);
      // Enrichment runs synchronously upstream and can take minutes, exceeding
      // the gateway timeout even though the record is usually already written.
      // Tell the user to verify instead of blindly retrying (which duplicates).
      if (isLikelyTimeoutError(e)) {
        setBanner(
          mode === "create"
            ? "The request timed out while enrichment was running, but the company may already have been created. Switch to “Update existing company” and fetch it, or search before creating again to avoid duplicates."
            : "The request timed out while enrichment was running, but your update may already have been applied. Fetch the company again to verify before retrying."
        );
      } else {
        setBanner(describeError(e));
      }
    },
  });

  const validate = (): FieldErrors => {
    const next: FieldErrors = {};
    const url = linkedInURL.trim();
    if (!url) {
      next.linkedInURL = "LinkedIn URL is required";
    } else if (!isValidLinkedInCompanyUrl(url)) {
      next.linkedInURL =
        "Must be a valid LinkedIn company URL (https://www.linkedin.com/company/...)";
    }

    if (mode === "create") {
      if (!companyName.trim()) next.companyName = "Company name is required";
    } else {
      const id = Number(companyID.trim());
      if (!companyID.trim()) next.companyID = "Company ID is required";
      else if (!Number.isInteger(id) || id <= 0)
        next.companyID = "Company ID must be numeric";
    }
    return next;
  };

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();
    setResult(null);
    setBanner(null);
    const validationErrors = validate();
    setErrors(validationErrors);
    if (Object.values(validationErrors).some(Boolean)) return;

    const url = linkedInURL.trim();
    if (mode === "create") {
      ingestMutation.mutate({ companyName: companyName.trim(), linkedInURL: url });
    } else {
      ingestMutation.mutate({
        companyID: Number(companyID.trim()),
        linkedInURL: url,
      });
    }
  };

  const errorInputClass = "border-destructive focus-visible:ring-destructive";

  return (
    <div className="space-y-6">
      <div className="space-y-1">
        <Link
          to="/dashboard/private-companies"
          className="inline-flex items-center gap-1 text-sm text-muted-foreground transition-colors hover:text-foreground"
        >
          <ArrowLeft className="h-4 w-4" />
          Private Companies
        </Link>
        <h1 className="text-2xl font-bold">LinkedIn Ingestion</h1>
        <p className="text-sm text-muted-foreground">
          Create a new private company or update an existing one with a LinkedIn URL.
        </p>
      </div>

      {/* Mode toggle */}
      <div className="inline-flex rounded-lg border bg-card p-1">
        <button
          type="button"
          onClick={() => switchMode("create")}
          className={cn(
            "rounded-md px-4 py-1.5 text-sm font-medium transition-colors",
            mode === "create"
              ? "bg-primary text-primary-foreground"
              : "text-muted-foreground hover:text-foreground"
          )}
        >
          Create new company
        </button>
        <button
          type="button"
          onClick={() => switchMode("update")}
          className={cn(
            "rounded-md px-4 py-1.5 text-sm font-medium transition-colors",
            mode === "update"
              ? "bg-primary text-primary-foreground"
              : "text-muted-foreground hover:text-foreground"
          )}
        >
          Update existing company
        </button>
      </div>

      {/* Error banner */}
      {banner && (
        <div className="rounded-lg border border-destructive/50 bg-destructive/10 px-4 py-3 text-sm text-destructive">
          {banner}
        </div>
      )}

      {/* Success result */}
      {result?.success && (
        <div className="rounded-lg border border-emerald-500/50 bg-emerald-500/10 px-4 py-3 text-sm">
          <div className="flex items-center gap-2 font-medium text-emerald-600 dark:text-emerald-400">
            <CheckCircle2 className="h-4 w-4" />
            {mode === "create"
              ? "Private company created successfully"
              : "Private company updated successfully"}
          </div>
          <dl className="mt-2 space-y-1 text-muted-foreground">
            <div className="flex gap-2">
              <dt className="font-medium text-foreground">Private company ID:</dt>
              <dd>{result.privateCompanyID ?? "—"}</dd>
            </div>
            <div className="flex gap-2">
              <dt className="font-medium text-foreground">Enrichment triggered:</dt>
              <dd>{result.enrichmentTriggered ? "Yes" : "No"}</dd>
            </div>
          </dl>
        </div>
      )}

      <form onSubmit={handleSubmit} className="max-w-md space-y-5">
        {mode === "create" ? (
          <div className="space-y-2">
            <Label htmlFor="companyName">Company name</Label>
            <Input
              id="companyName"
              value={companyName}
              onChange={(e) => setCompanyName(e.target.value)}
              placeholder="Example Company"
              className={cn(errors.companyName && errorInputClass)}
              aria-invalid={Boolean(errors.companyName)}
            />
            {errors.companyName && (
              <p className="text-xs text-destructive">{errors.companyName}</p>
            )}
          </div>
        ) : (
          <div className="space-y-2">
            <Label htmlFor="companyID">Company ID</Label>
            <div className="flex gap-2">
              <Input
                id="companyID"
                inputMode="numeric"
                value={companyID}
                onChange={(e) => {
                  setCompanyID(e.target.value);
                  setFetched(null);
                }}
                placeholder="12345"
                className={cn(errors.companyID && errorInputClass)}
                aria-invalid={Boolean(errors.companyID)}
              />
              <Button
                type="button"
                variant="outline"
                onClick={handleFetchCompany}
                disabled={fetchMutation.isPending}
              >
                {fetchMutation.isPending ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Download className="h-4 w-4" />
                )}
                Fetch
              </Button>
            </div>
            {errors.companyID && (
              <p className="text-xs text-destructive">{errors.companyID}</p>
            )}
            {fetched && (
              <div className="rounded-lg border bg-muted/40 px-3 py-2 text-sm">
                <div className="flex gap-2">
                  <span className="font-medium">Company name:</span>
                  <span className="text-muted-foreground">
                    {fetched.companyName || "—"}
                  </span>
                </div>
                <div className="flex gap-2">
                  <span className="font-medium">Existing LinkedIn:</span>
                  <span className="break-all text-muted-foreground">
                    {fetched.linkedIn || "None"}
                  </span>
                </div>
              </div>
            )}
          </div>
        )}

        <div className="space-y-2">
          <Label htmlFor="linkedInURL">LinkedIn URL</Label>
          <Input
            id="linkedInURL"
            value={linkedInURL}
            onChange={(e) => setLinkedInURL(e.target.value)}
            placeholder={LINKEDIN_PLACEHOLDER}
            className={cn(errors.linkedInURL && errorInputClass)}
            aria-invalid={Boolean(errors.linkedInURL)}
          />
          {errors.linkedInURL ? (
            <p className="text-xs text-destructive">{errors.linkedInURL}</p>
          ) : (
            <p className="text-xs text-muted-foreground">
              Must match https://www.linkedin.com/company/...
            </p>
          )}
        </div>

        <div className="pt-2">
          <Button type="submit" disabled={ingestMutation.isPending}>
            {ingestMutation.isPending && (
              <Loader2 className="h-4 w-4 animate-spin" />
            )}
            {mode === "create" ? "Create company" : "Update company"}
          </Button>
        </div>
      </form>
    </div>
  );
}
